import React from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Chip,
} from '@nextui-org/react';
import { FaEye, FaEdit, FaTrash, FaComment } from 'react-icons/fa';
import { formatPrice } from 'common/src/utils/functions';
import { Tasks } from 'common/src/utils/types';
import { useCategories } from '../../../../contexts/CategoriesContext';

interface TaskCardProps {
  cardTitle: string;
  cardCategory: string;
  onClick: () => void;
  assignedUserInitials: string;
  assignedUserName: string;
  price: number;
  date: string;
  onEdit: () => void;
  onDelete: () => void;
  name: string;
  last_name: string;
  phone_number: string;
  onAddComment: () => void;
  task: Partial<Tasks> & {
    hasPayment?: boolean;
    paymentStatus?: string | null;
    payment_method?: string | null;
  };
}

export const TaskCard: React.FC<TaskCardProps> = ({
  cardTitle,
  cardCategory,
  phone_number,
  onClick,
  assignedUserInitials,
  assignedUserName,
  price,
  date,
  onEdit,
  onDelete,
  name,
  last_name,
  onAddComment,
  task,
}) => {
  const { categories } = useCategories();

  console.log("task.category_id", task.category_id)
  
  const getTitle = () => {
    if (cardTitle) return cardTitle;
    if (task.category_id && categories.length > 0) {
      const category = categories.find(cat => cat.id === task.category_id);
      return category?.name || 'Sin categoría';
    }
    return 'Sin título';
  };

  const getPaymentChipProps = () => {
    if (
      task.payment_method &&
      task.payment_method.toLowerCase().includes('card')
    ) {
      if (!task.hasPayment) {
        return {
          color: 'default' as const,
          className: 'bg-gray-200 text-gray-800',
          text: 'Card - No Pagado',
        };
      } else if (task.hasPayment && task.paymentStatus === 'approved') {
        return {
          color: 'success' as const,
          className: 'bg-green-200 text-green-800',
          text: 'Card - Pagado',
        };
      } else if (task.hasPayment && task.paymentStatus === 'rejected') {
        return {
          color: 'danger' as const,
          className: 'bg-red-200 text-red-800',
          text: 'Card - Rechazado',
        };
      } else if (task.hasPayment && task.paymentStatus === 'failed') {
        return {
          color: 'warning' as const,
          className: 'bg-orange-200 text-orange-800',
          text: 'Card - Fallido',
        };
      } else {
        return {
          color: 'secondary' as const,
          className: 'bg-purple-200 text-purple-800',
          text: 'Card - Otro',
        };
      }
    }
    return null;
  };

  return (
    <Card className="max-w-full p-2 relative">
      <CardHeader>
        <div className="flex flex-col w-full">
          <div className="flex items-center justify-between ">
            <p className="font-semibold">{getTitle()}</p>
            <p className="text-sm  text-gray-500">{date}</p>
          </div>
          <div className="mt-2 flex items-center space-x-2">
            <Chip
              color="primary"
              className="bg-purple-200 text-purple-800"
              variant="flat"
              size="md"
            >
              {formatPrice(price)}
            </Chip>
            {task.is_asap && (
              <Chip
                color="danger"
                className="bg-red-200 text-red-800"
                variant="flat"
                size="md"
              >
                ASAP!
              </Chip>
            )}
            {task.is_flexible_date && (
              <Chip
                color="warning"
                className="bg-yellow-200 text-yellow-800"
                variant="flat"
                size="md"
              >
                Fecha Flexible
              </Chip>
            )}
            {getPaymentChipProps() && (
              <Chip
                color={getPaymentChipProps()!.color}
                className={getPaymentChipProps()!.className}
                variant="flat"
                size="md"
              >
                {getPaymentChipProps()!.text}
              </Chip>
            )}
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <div className="flex flex-col">
          <div className="flex items-center justify-between pb-2">
            <p>
              {name} {last_name}
            </p>
            <p className="text-neutral-500 text-sm">{phone_number}</p>
          </div>
          <p className="text-neutral-500 line-clamp-2">{cardCategory}</p>
        </div>
      </CardBody>
      <CardFooter className="relative">
        <div className="flex space-x-2">
          <button
            className="text-neutral-400 hover:text-neutral-600 focus:outline-none"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClick();
            }}
            title="Ver más"
          >
            <FaEye size={20} />
          </button>
          <button
            className="text-neutral-400 hover:text-neutral-600 focus:outline-none"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEdit();
            }}
            title="Editar"
          >
            <FaEdit size={20} />
          </button>
          <button
            className="text-neutral-400 hover:text-neutral-600 focus:outline-none"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAddComment();
            }}
            title="Agregar Comentario"
          >
            <FaComment size={20} />
          </button>
          <button
            className="text-neutral-400 hover:text-neutral-600 focus:outline-none"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete();
            }}
            title="Delete"
          >
            <FaTrash size={16} />
          </button>
        </div>
      </CardFooter>
      <div className="flex items-center">
        <div
          className="absolute bottom-2.5 right-2.5 bg-purple-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold cursor-pointer hover:after:content-[attr(title)] hover:after:absolute hover:after:bottom-10 hover:after:right-0 hover:after:bg-gray-800 hover:after:text-white hover:after:py-1 hover:after:px-2.5 hover:after:rounded hover:after:whitespace-nowrap hover:after:z-10"
          title={assignedUserName}
        >
          {assignedUserInitials}
        </div>
      </div>
    </Card>
  );
};
